import React, { useState, useEffect } from "react";
import "./ContactForm.css";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

const countryCodes = [
  "AF",
  "AL",
  "DZ",
  "US",
  "AD",
  "AO",
  "AR",
  "AM",
  "AU",
  "AT",
  "AZ",
  "BD",
  "BY",
  "BE",
  "BG",
  "BO",
  "BR",
  "BN",
  "BA",
  "BW",
  "CA",
  "CL",
  "CN",
  "CO",
  "CR",
  "HR",
  "CU",
  "CY",
  "CZ",
  "DK",
  "EG",
  "EE",
  "FI",
  "FR",
  "GE",
  "DE",
  "GR",
  "HU",
  "IS",
  "IN",
  "ID",
  "IR",
  "IQ",
  "IE",
  "IL",
  "IT",
  "JP",
  "KZ",
  "KE",
  "KR",
  "KW",
  "KG",
  "LA",
  "LV",
  "LB",
  "LT",
  "LU",
  "MY",
  "MT",
  "MX",
  "MD",
  "MC",
  "MN",
  "MA",
  "NL",
  "NZ",
  "NE",
  "NG",
  "MK",
  "NO",
  "PK",
  "PA",
  "PY",
  "PE",
  "PH",
  "PL",
  "PT",
  "RO",
  "RU",
  "SA",
  "SG",
  "SK",
  "SI",
  "ZA",
  "ES",
  "SE",
  "CH",
  "TW",
  "TJ",
  "TH",
  "TR",
  "UA",
  "AE",
  "GB",
  "UY",
  "UZ",
  "VA",
  "VE",
  "VN",
  "YE",
  "ZM",
  "ZW",
];

const validateEmail = (email) => /^[^@]+@[^@]+\.[^@]+$/.test(email);
const validatePhone = (phone) => /^[0-9]{8}$/.test(phone);
const hasDangerousChars = (str) => /[<>]/.test(str);

const FREIGHT_TYPES = [
  { id: "air" },
  { id: "sea" },
  { id: "road" },
  { id: "rail" },
  { id: "export" },
  { id: "transit" },
  { id: "cargo" },
  { id: "other" },
];

const getInitialFormData = (freightTypeId) => ({
  from: "",
  name: "",
  to: "",
  phone: "",
  email: "",
  freight_type: freightTypeId,
  additional: "",
});

const ContactForm = () => {
  const { freightId, lang } = useParams();
  const { t, i18n } = useTranslation();

  // Set language based on URL parameter
  useEffect(() => {
    if (lang && (lang === "mn" || lang === "en")) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  const selectedFreight = FREIGHT_TYPES.find((f) => f.id === freightId);
  const initialFreightType = selectedFreight
    ? selectedFreight.id
    : FREIGHT_TYPES[0].id;

  const [formData, setFormData] = useState(
    getInitialFormData(initialFreightType)
  );

  useEffect(() => {
    setFormData(getInitialFormData(initialFreightType));
  }, [initialFreightType]);

  const [toast, setToast] = useState({ message: "", type: "" });
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone") {
      // Зөвхөн тоо болон + - ( ) тэмдэгт зөвшөөрнө
      const filtered = value.replace(/[^0-9+\-() ]/g, "");
      setFormData((prevState) => ({
        ...prevState,
        [name]: filtered,
      }));
    } else {
      setFormData((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    showToast(""); // reset

    if (!formData.name || hasDangerousChars(formData.name)) {
      showToast(t("messages.name_error"));
      return;
    }
    if (!formData.email || !validateEmail(formData.email)) {
      showToast(t("messages.email_error"));
      return;
    }
    if (!formData.phone || !validatePhone(formData.phone)) {
      showToast(t("messages.phone_error"), "error");
      return;
    }
    if (formData.additional && hasDangerousChars(formData.additional)) {
      showToast(t("messages.additional_error"));
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(
        "https://odoo.mlholding.mn/api/crm/partner/lead",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            from: formData.from,
            to: formData.to,
            additional: formData.additional || "",
            freight_type: formData.freight_type || "",
          }),
        }
      );
      if (!response.ok) throw new Error("Failed to submit form");
      setFormData(getInitialFormData(initialFreightType));
      setShowSuccess(true);
    } catch (error) {
      console.error("Error submitting form:", error);
      showToast(t("messages.error"), "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const showToast = (msg, type = "success") => {
    setToast({ message: msg, type });
    setTimeout(() => setToast({ message: "", type: "" }), 2000);
  };

  const dismissSuccess = () => setShowSuccess(false);

  return (
    <div className="form-container">
      <div className="form-shell">
        <div
          className={showSuccess ? "form-body--dimmed" : undefined}
          inert={showSuccess ? true : undefined}
        >
          <form
            onSubmit={handleSubmit}
            aria-hidden={showSuccess}
            aria-label={t("title")}
          >
        <div className="form-group">
          <label htmlFor="from">{t("from")}</label>
          <select
            id="from"
            name="from"
            value={formData.from}
            onChange={handleChange}
          >
            <option value="">{t("Choose country")}</option>
            {countryCodes.map((code) => (
              <option key={code} value={code}>
                {t(`countries.${code}`)}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="name">
            {t("name")} <span className="required">{t("required")}</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            placeholder={t("name")}
            value={formData.name}
            onChange={handleChange}
            required
            maxLength={100}
          />
        </div>
        <div className="form-group">
          <label htmlFor="to">{t("to")}</label>
          <select id="to" name="to" value={formData.to} onChange={handleChange}>
            <option value="">{t("Choose country")}</option>
            {countryCodes.map((code) => (
              <option key={code} value={code}>
                {t(`countries.${code}`)}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="phone">
            {t("phone")} <span className="required">{t("required")}</span>
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            placeholder={t("phone")}
            value={formData.phone}
            onChange={handleChange}
            required
            pattern="[0-9+\-() ]*"
            maxLength={20}
          />
        </div>
        <div className="form-group">
          <label htmlFor="email">
            {t("email")} <span className="required">{t("required")}</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            placeholder={t("email")}
            value={formData.email}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="freight_type">{t("freight_type")}</label>
          <select
            id="freight_type"
            name="freight_type"
            value={formData.freight_type}
            onChange={handleChange}
          >
            {FREIGHT_TYPES.map((type) => (
              <option key={type.id} value={type.id}>
                {t(`freight_types.${type.id}`)}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group full-width">
          <label htmlFor="additional">{t("additional")}</label>
          <input
            type="text"
            id="additional"
            name="additional"
            placeholder={t("additional")}
            value={formData.additional}
            onChange={handleChange}
          />
        </div>
        <button
          type="submit"
          className="submit-button"
          disabled={isSubmitting}
          aria-busy={isSubmitting}
        >
          {isSubmitting ? t("submitting") : t("submit")}
        </button>
      </form>
        </div>
        {showSuccess ? (
          <div className="success-overlay">
            <div
              className="success-panel"
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="success-message-text"
            >
              <div className="success-panel-inner">
                <div className="success-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="36" height="36" fill="none">
                    <circle cx="12" cy="12" r="11" stroke="currentColor" strokeWidth="1.5" />
                    <path
                      d="M8 12.5l2.5 2.5L16 9"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <p id="success-message-text" className="success-message">
                  {t("messages.success")}
                </p>
                <div className="success-actions">
                  <button
                    type="button"
                    className="success-btn success-btn-primary"
                    onClick={dismissSuccess}
                  >
                    {t("messages.success_got_it")}
                  </button>
                  <button
                    type="button"
                    className="success-btn success-btn-secondary"
                    onClick={dismissSuccess}
                  >
                    {t("messages.success_close")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
      {toast.message && (
        <div className={`toast-notif ${toast.type}`}>{toast.message}</div>
      )}
    </div>
  );
};

export default ContactForm;
